import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { SketchPicker } from "react-color";
import { useSnackbar } from "notistack";
import MDButton from "components/MDButton";
import {
  useCreateGeofenceMutation,
  useUpdateGeofenceMutation,
  useGetPhoneBookByDepartmentMutation,
} from "features/geofenceSlice";
import AuthenticationService from "application/modules/authentication/AuthenticationService";

const DEFAULT_COLOR = "#3388ff";

export default function GeofenceEditorDialog({
  open,
  shape,
  geofence,
  onColorChange,
  onClose,
  onSaved,
}) {
  const isEditing = Boolean(geofence?.id);

  const [name, setName] = useState("");
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [colorAnchor, setColorAnchor] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]); // phone-book extensions
  const [selectedDepartments, setSelectedDepartments] = useState([]); // department names
  const [userOptions, setUserOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  const [createGeofence, { isLoading: isCreating }] = useCreateGeofenceMutation();
  const [updateGeofence, { isLoading: isUpdating }] = useUpdateGeofenceMutation();
  const [getPhoneBookByDepartment] = useGetPhoneBookByDepartmentMutation();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!open) return undefined;

    let isMounted = true;

    setName(geofence?.name || "");
    setColor(geofence?.color || DEFAULT_COLOR);
    setSelectedUsers(geofence?.assignedUsers || []);
    setSelectedDepartments(geofence?.assignedDepartments || []);

    // Departments the dispatcher is allowed to see (from the login session)
    const departments = AuthenticationService.getDepartmentList() || [];
    const seen = new Set();
    const uniqueDepartments = [];
    departments.forEach((dept) => {
      const deptName = dept?.department;
      if (deptName && !seen.has(deptName)) {
        seen.add(deptName);
        uniqueDepartments.push(dept);
      }
    });
    setDepartmentOptions(uniqueDepartments);

    // Individual users come from the phone book, filtered to those departments
    const departmentNames = uniqueDepartments.map((d) => d.department).filter(Boolean);
    if (departmentNames.length) {
      getPhoneBookByDepartment({ searchRequest: {}, departmentList: departmentNames })
        .unwrap()
        .then((list) => {
          if (isMounted) setUserOptions(Array.isArray(list) ? list : []);
        })
        .catch(() => {
          if (isMounted) setUserOptions([]);
        });
    } else {
      setUserOptions([]);
    }

    return () => {
      isMounted = false;
    };
  }, [open, geofence]);

  const handleColorChange = (value) => {
    setColor(value.hex);
    onColorChange?.(value.hex);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      enqueueSnackbar("Please enter a name for the geofence", { variant: "warning" });
      return;
    }

    const shapeSource = geofence || shape || {};
    const payload = {
      id: geofence?.id,
      name: name.trim(),
      color,
      shapeType: shapeSource.shapeType,
      coordinates: shapeSource.coordinates,
      centerLat: shapeSource.centerLat,
      centerLng: shapeSource.centerLng,
      radiusMeters: shapeSource.radiusMeters,
      assignedUsers: selectedUsers, // extensions
      assignedDepartments: selectedDepartments, // department names
    };

    try {
      if (isEditing) {
        await updateGeofence(payload).unwrap();
        enqueueSnackbar("Geofence updated successfully", { variant: "success" });
      } else {
        await createGeofence(payload).unwrap();
        enqueueSnackbar("Geofence created successfully", { variant: "success" });
      }
      onSaved?.();
    } catch (error) {
      enqueueSnackbar("Unable to save geofence", { variant: "error" });
    }
  };

  const userLabel = (option) =>
    `${option.name || option.designation || option.offEXtn || ""}${
      option.offEXtn ? ` (${option.offEXtn})` : ""
    }`;

  const departmentLabel = (option) => option.departmentDescription || option.department || "";

  const userOptionList = userOptions.filter((u) => u.offEXtn);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{isEditing ? "Edit Geofence" : "Create Geofence"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <TextField
          label="Geofence Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          autoFocus
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="body2">Color</Typography>
          <Box
            onClick={(e) => setColorAnchor(e.currentTarget)}
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: color,
              border: "2px solid #ccc",
              cursor: "pointer",
            }}
          />
        </Box>
        <Popover
          open={Boolean(colorAnchor)}
          anchorEl={colorAnchor}
          onClose={() => setColorAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <SketchPicker color={color} disableAlpha onChangeComplete={handleColorChange} />
        </Popover>

        <Autocomplete
          multiple
          options={userOptionList}
          getOptionLabel={userLabel}
          isOptionEqualToValue={(option, value) => option.offEXtn === value.offEXtn}
          value={userOptionList.filter((u) => selectedUsers.includes(u.offEXtn))}
          onChange={(e, value) => setSelectedUsers(value.map((v) => v.offEXtn))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option.name || option.offEXtn}
                {...getTagProps({ index })}
                key={option.offEXtn}
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Assign to Users" placeholder="Select users" />
          )}
        />

        <Autocomplete
          multiple
          options={departmentOptions}
          getOptionLabel={departmentLabel}
          isOptionEqualToValue={(option, value) => option.department === value.department}
          value={departmentOptions.filter((d) => selectedDepartments.includes(d.department))}
          onChange={(e, value) => setSelectedDepartments(value.map((v) => v.department))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={departmentLabel(option)}
                {...getTagProps({ index })}
                key={option.department}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Assign to Departments"
              placeholder="Select departments"
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary">
          Cancel
        </MDButton>
        <MDButton onClick={handleSave} color="info" disabled={isCreating || isUpdating}>
          Save
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
